/**
 * Room chat: the messages tomo carries itself.
 *
 * Everything else in a room's timeline is MIRRORED Twitch chat, which arrives
 * at each browser directly from Twitch and is the streamer's to moderate. This
 * is the other thing — messages written in tomo, to the room, by people signed
 * in to tomo. ADR 0038 allows it only because somebody can remove one; ADR
 * 0042 says who, and draws the line that makes these two kinds of message
 * behave differently in the same column.
 *
 * WHY AN AUTHOR IS DENORMALISED ONTO THE MESSAGE. A room's timeline is read
 * far more often than it is written and a reader needs a name and a face for
 * every row. Joining to the user document per message would be a read per
 * message per viewer; storing the handle and avatar at send time is one write.
 * The cost is that a renamed account keeps its old name on old messages, which
 * is the correct answer anyway — it is what they were called when they said
 * it.
 *
 * WHY THERE IS NO `edited`. There is no edit. A message that could be changed
 * after somebody replied to it is a moderation problem wearing a feature's
 * clothes, and the predecessors both grew one.
 */
import { z } from 'zod';
import { RoomId } from './room.js';

export const RoomMessageId = z
  .string()
  .regex(/^rmsg_[a-z0-9]{6,30}$/, 'A room message id looks like rmsg_xxxxxxxx');

/**
 * 500 characters, which is Twitch's own limit.
 *
 * Matching it is not deference: the composer is ONE box that sends either to a
 * Twitch channel or to the room, and a limit that changed depending on the
 * target would let somebody type a message they cannot send and only find out
 * at the end of it.
 */
export const ROOM_MESSAGE_MAX = 500;

/** Enough of the parent to recognise it; not enough to re-read it. */
export const REPLY_EXCERPT_MAX = 120;

export const RoomMessage = z.object({
  id: RoomMessageId,
  room_id: RoomId,
  /** The author's tomo account. Always present: room chat requires a login. */
  uid: z.string().min(1),
  /**
   * Twitch login at send time, lower case. For DISPLAY: people rename, so
   * `uid` is who sent it and this is only what they were called then.
   */
  handle: z.string().min(1),
  display_name: z.string().min(1),
  avatar_url: z.string().url().nullable(),
  body: z.string().min(1).max(ROOM_MESSAGE_MAX),
  /**
   * The message this answers, flattened rather than nested.
   *
   * The AUTHOR and a short excerpt are stored on the reply, not just the
   * parent's id. A nested read would be a lookup per reply on a timeline that
   * renders hundreds of them — and worse, a parent removed by a moderator
   * would take every reply's context with it, so a thread would lose its
   * shape retroactively. What was being answered is a fact about the reply.
   */
  reply_to: z
    .object({
      message_id: RoomMessageId,
      display_name: z.string().min(1),
      excerpt: z.string().max(REPLY_EXCERPT_MAX),
    })
    .nullable()
    .optional(),
  sent_at: z.string().datetime(),
});

/**
 * A room message as the PUBLIC feed carries it (ADR 0048).
 *
 * The same message less the author's account id. The feed is read by anybody,
 * signed in or not, and ends up on stream overlays; `uid` identifies an
 * account for moderation and blocking, and a display needs neither. `handle`
 * is already public — it is the Twitch login shown beside the message.
 */
export const RoomFeedMessage = RoomMessage.omit({ uid: true });

/** The feed's opening frame. `resumed` says whether a gap was just filled. */
export const RoomFeedHello = z.object({
  room_id: RoomId,
  resumed: z.boolean(),
  lifetime_ms: z.number().int().positive(),
});

/** "Remove this if you are showing it." Idempotent by design. */
export const RoomFeedDeleted = z.object({ id: RoomMessageId });

export const RoomMessageInput = z.object({
  body: z.string().min(1).max(ROOM_MESSAGE_MAX),
  /**
   * What this answers, by id.
   *
   * Only the id crosses the wire: the server reads the parent and copies the
   * author and excerpt itself, so a client cannot attribute a reply to
   * somebody who never said it.
   */
  reply_to: RoomMessageId.nullable().optional(),
});

/**
 * Silencing somebody in ONE room, until a moment.
 *
 * Timed rather than indefinite, and the expiry is stored rather than computed
 * from a duration, because a mute whose end is a duration has to be re-read
 * against the moment it started every time anyone asks. An `until` answers the
 * only question a mute is ever asked: may this person speak now.
 *
 * A mute is never retroactive. It stops the next message; it does not remove
 * the ones already said, which is a delete and a separate decision.
 */
export const RoomMute = z.object({
  room_id: RoomId,
  uid: z.string().min(1),
  handle: z.string().min(1).nullable(),
  /** ISO. A mute with no end is expressed as a very distant one, never null. */
  until: z.string().datetime(),
  /**
   * True when they were also ejected: presence row dropped and stream closed.
   *
   * One row rather than two collections, because kicking somebody who may
   * immediately walk back in is not a kick. A kick is a mute you are also not
   * present during, and modelling it as its own thing produced two expiries to
   * keep in step.
   */
  ejected: z.boolean(),
  by_uid: z.string().min(1),
  reason: z.string().max(200).nullable(),
  created_at: z.string().datetime(),
});

/**
 * How far in the future a block reaches.
 *
 * Not `null` and not a flag on the stored row: `until` is an instant on every
 * mute, so everything that reads one compares two dates and nothing has to
 * ask which kind it is holding. A block is simply a mute that outlives the
 * argument.
 */
export const FOREVER = '9999-12-31T23:59:59.000Z';

/**
 * Minutes, so an interface offers 5 / 60 / a day rather than an ISO string —
 * or `forever`, which is what a community's block list is made of.
 *
 * `minutes` used to be required and capped at a year, which meant the one
 * thing an admin most wants to express could not be said at all. It is
 * optional now rather than removed: every existing caller still sends it, and
 * a timed mute is still the common case in a moderation menu.
 */
export const RoomMuteInput = z
  .object({
    minutes: z.number().int().min(1).max(60 * 24 * 365).optional(),
    /** A block: no end, and it survives them rejoining. */
    forever: z.boolean().optional(),
    eject: z.boolean().optional(),
    reason: z.string().max(200).optional(),
  })
  .refine((v) => (v.minutes === undefined) !== (v.forever !== true), {
    message: 'Give either minutes or forever, not both and not neither.',
  });

export type RoomMessage = z.infer<typeof RoomMessage>;
export type RoomMessageInput = z.infer<typeof RoomMessageInput>;
export type RoomFeedMessage = z.infer<typeof RoomFeedMessage>;
export type RoomFeedHello = z.infer<typeof RoomFeedHello>;
export type RoomFeedDeleted = z.infer<typeof RoomFeedDeleted>;
export type RoomMute = z.infer<typeof RoomMute>;
export type RoomMuteInput = z.infer<typeof RoomMuteInput>;
